import { Item } from '@radix-ui/react-dropdown-menu'
import clsx from 'clsx'
import React, { useContext, useState } from 'react'

import { type IconProp } from 'slab/types'

import { DropdownMenuContext } from './context'

export interface DropdownMenuItemProps {
  children: React.ReactNode;
  disabled?: boolean;
  leftIcon?: IconProp;
  rightIcon?: IconProp;
  /**
   * A callback that is invoked when the item is selected. The menu will close
   * automatically after the callback is executed, unless the callback returns
   * `false`. If the callback is async or returns a promise, the menu will close
   * after it resolves, unless the promise resolves to `false`.
   *
   * The callback is also passed a `closeMenu` function that can be called to
   * manually close the menu. If using `closeMenu`, be sure to also return
   * `false` from the callback.
   */
  onSelect?: (
    closeMenu: () => void
  ) => (boolean | void) | Promise<boolean | void>;
  type?: 'normal' | 'destructive';
}

export function DropdownMenuItem ({
  children,
  disabled,
  leftIcon: LeftIcon,
  rightIcon: RightIcon,
  onSelect,
  type = 'normal',
}: DropdownMenuItemProps) {
  const [isPending, setPending] = useState(false)

  const { closeMenu } = useContext(DropdownMenuContext)

  return (
    <Item
      className='py-0.5 px-2 group/menuitem outline-none'
      disabled={isPending || disabled}
      onSelect={async (event) => {
        if (typeof onSelect === 'function') {
          // Prevent the Radix UI primitive from closing the menu automatically
          event.preventDefault()

          setPending(true)
          const result = await onSelect(closeMenu)
          setPending(false)

          if (result !== false) closeMenu()
        }
      }}
    >
      <div
        className={clsx(
          'p-2 rounded-md bg-transparent flex gap-4 items-center body transition cursor-pointer group-data-[disabled]/menuitem:cursor-default group-data-[disabled]/menuitem:text-gray-800/40 group-data-[disabled]/menuitem:group-hover/menuitem:bg-transparent',
          {
            'text-gray-800 group-hover/menuitem:bg-gray-50 group-data-[highlighted]/menuitem:bg-gray-100':
              type === 'normal',
            'text-red-700 group-hover/menuitem:bg-red-50 group-data-[highlighted]/menuitem:bg-red-100':
              type === 'destructive',
          }
        )}
      >
        <div className='flex gap-2 items-center'>
          {LeftIcon && (
            <div
              className={clsx(
                'transition group-data-[disabled]/menuitem:text-gray-800/30',
                {
                  'text-gray-500 group-hover/menuitem:text-gray-600':
                    type === 'normal',
                  'text-red-500 group-hover/menuitem:text-red-600':
                    type === 'destructive',
                }
              )}
            >
              <LeftIcon aria-hidden size={16} />
            </div>
          )}
          {children}
        </div>
        {/* We're always leaving space for the right icon, even if it was not
         * not provided, so that right icons align nicely in menus that
         * contain a mix of items with and without right icons.
         */}
        <div className='ml-auto flex items-center justify-end w-4'>
          {RightIcon && (
            <div className='transition text-gray-500 group-hover/menuitem:text-gray-600 group-data-[disabled]/menuitem:text-gray-800/30'>
              <RightIcon aria-hidden size={16} />
            </div>
          )}
        </div>
      </div>
    </Item>
  )
}
